import { Grid2, Stack, Typography } from '@mui/material';
import React from 'react';

import Icon from '@components/Icon/Icon';

import useReferenceRange from '@hooks/useReferenceRange';

import type { ReferenceRangeRiskLevel } from 'generated/types';

interface RangeListProps {
  /** Unique identifier of metric */
  code: string;
  /** Friendly text of level */
  levelText: string;
  /** Level: normal | moderate | high */
  level: ReferenceRangeRiskLevel;
  /** Array of friendly ranges
   * E.g. ['130 – 140', 'or', '80 – 90']
   * E.g. ['Below 11']
   */
  range: string[];
  /** Is the patient's result within this range? */
  isInRange: boolean;
}

export default function RangeList({
  code,
  level,
  levelText,
  range,
  isInRange,
}: RangeListProps) {
  const { parameterMap } = useReferenceRange();
  const { colour, bgColour } = parameterMap[level];

  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      px={2}
      my={0.75}
      py={0.5}
      sx={({ palette }) => ({
        /* If the metric is within range, change the bg color according to risk level
                LOW = green, MED = orange, HIGH = red
                Else keep the bg colour as grey
                */
        backgroundColor: isInRange ? bgColour : palette.grey['100'],
        borderRadius: '6px',
        /* If the metric is within range, outline the border */
        ...(isInRange && {
          border: `1px solid`,
          borderColor: colour,
        }),
      })}
    >
      <Grid2 container spacing={1}>
        {range.map((num, numIdx) => (
          <Grid2 key={`${code}-ref-value-${num}`}>
            <Typography
              fontWeight={
                numIdx !== 0 && range.length - 1 !== numIdx ? 'bold' : undefined
              }
              variant="body2"
            >
              {num}
            </Typography>
          </Grid2>
        ))}
      </Grid2>

      {/* Low/Medium/High risk text */}
      <Typography sx={{ width: '60%' }} variant="body2">
        <Icon color={colour} name="square" size="xl" type="fas" /> {levelText}
      </Typography>
    </Stack>
  );
}
